import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
    Box,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { GlobalState } from "context/GlobalContext";

export default function DiscountActionPage() {

    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const action = searchParams.get("action");

    const [status, setStatus] = useState("");
    const [message, setSMessage] = useState("");

    const [error, setError] = useState("");

    const [reason, setReason] = useState("");
    const [remarks, setRemarks] = useState("");
    const [approvalFile, setApprovalFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [submitError, setSubmitError] = useState("");

    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const [openApproveDialog, setOpenApproveDialog] = useState(
        action === "approve" && Boolean(token)
    );
    const { setLoadingOpen } = GlobalState();


    // ==========================================
    // USE EFFECT
    // ==========================================

    useEffect(() => {

        if (!token || !action) {

            setError("Invalid Link");

            return;
        }

        // APPROVE FLOW
        if (action === "approve") {

            setOpenApproveDialog(true);
        }
        if (action === "force-close-approve") {

            forceClseApproveDiscount();
        }
        if (action === "force-close-reject") {

            forceClseRejectDiscount();
        }

        // REJECT FLOW
        if (action === "reject") {

            setOpenRejectDialog(true);
        }

        if (action === "download-attachment") {
            downloadEmailAttachment();
        }

    }, []);

    const downloadEmailAttachment = () => {
        const pythonApiUrl = String(
            process.env.REACT_APP_PYTHON_APP_API_URL || ""
        ).replace(/\/$/, "");

        if (!pythonApiUrl) {
            setError("Download link is not configured");
            return;
        }

        setStatus("downloading");
        setSMessage("Your file download should start shortly. Login is not required.");
        window.location.replace(
            `${pythonApiUrl}/discount-master/email-attachment?token=${encodeURIComponent(token)}`
        );
    };

    // ==========================================
    // APPROVE API
    // ==========================================

    const resolveActionStatus = (data, fallback = "approved") => {
        const apiMessage = String(data?.message || "");
        if (
            data?.already_published ||
            /published already|already approved|already approved and published/i.test(apiMessage)
        ) {
            return "already_approved";
        }
        if (data?.already_rejected || /rejected already|already rejected/i.test(apiMessage)) {
            return "already_rejected";
        }
        if (data?.already_closed || /already closed/i.test(apiMessage)) {
            return "already_closed";
        }
        if (data?.success === false) {
            return "error";
        }
        return fallback;
    };

    const onApprovalFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            setApprovalFile(null);
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            setApprovalFile(null);
            setFileError("File must be 20 MB or smaller");
            event.target.value = "";
            return;
        }

        setFileError("");
        setSubmitError("");
        setApprovalFile(file);
    };

    const approveDiscount = async () => {
        const trimmedRemarks = remarks.trim();

        try {
            setLoadingOpen(true);
            setSubmitError("");

            const form = new FormData();
            form.append("token", token);
            form.append("remarks", trimmedRemarks);
            if (approvalFile) {
                form.append("file", approvalFile);
            }

            const res = await pythonVmInstance.patch(
                "/discount-master/approve",
                form
            );

            setStatus(resolveActionStatus(res.data, "approved"));
            setSMessage(res.data.message);
            setOpenApproveDialog(false);
            setLoadingOpen(false);

        } catch (err) {

            setSubmitError(
                err?.response?.data?.message ||
                "Approval Failed"
            );
            setLoadingOpen(false);

        }
    };
    const forceClseApproveDiscount = async () => {

        try {
            setLoadingOpen(true);

            const res=await pythonVmInstance.patch(
                "/discount-master/force-close/approve",
                {
                    token
                }
            );

            setStatus(resolveActionStatus(res.data, "approved"));
            setSMessage(res.data.message);
            setLoadingOpen(false);

        } catch (err) {

            setError(
                err?.response?.data?.message ||
                "Approval Failed"
            );
            setLoadingOpen(false);

        }
    };
    const forceClseRejectDiscount = async () => {

        try {
            setLoadingOpen(true);

            const res=await pythonVmInstance.patch(
                "/discount-master/force-close/reject",
                {
                    token
                }
            );

            setStatus(resolveActionStatus(res.data, "rejected"));
            setSMessage(res.data.message);
            setLoadingOpen(false);

        } catch (err) {

            setError(
                err?.response?.data?.message ||
                "Approval Failed"
            );
            setLoadingOpen(false);

        }
    };

    // ==========================================
    // REJECT API
    // ==========================================

    const rejectDiscount = async () => {

        try {

            setLoadingOpen(true);

            const res = await pythonVmInstance.patch(
                "/discount-master/reject",
                {
                    token,
                    reason
                }
            );

            setStatus(resolveActionStatus(res.data, "rejected"));
            setSMessage(res.data.message);

            setOpenRejectDialog(false);
            setLoadingOpen(false);

        } catch (err) {

            setError(
                err?.response?.data?.message ||
                "Reject Failed"
            );
            setLoadingOpen(false);

        }
    };

    const statusConfig = {
        approved: {
            icon: CheckCircleIcon,
            title: "Discount Approved",
            color: "#2e7d32",
            bg: "#e8f5e9",
            border: "#a5d6a7",
        },
        already_approved: {
            icon: InfoOutlinedIcon,
            title: "Already Approved",
            color: "#ed6c02",
            bg: "#fff4e5",
            border: "#ffcc80",
        },
        rejected: {
            icon: CancelIcon,
            title: "Discount Rejected",
            color: "#c62828",
            bg: "#ffebee",
            border: "#ef9a9a",
        },
        already_rejected: {
            icon: InfoOutlinedIcon,
            title: "Already Rejected",
            color: "#ed6c02",
            bg: "#fff4e5",
            border: "#ffcc80",
        },
        already_closed: {
            icon: InfoOutlinedIcon,
            title: "Already Closed",
            color: "#ed6c02",
            bg: "#fff4e5",
            border: "#ffcc80",
        },
        error: {
            icon: ErrorOutlineIcon,
            title: "Action Failed",
            color: "#d32f2f",
            bg: "#fff5f5",
            border: "#f5c2c7",
        },
        downloading: {
            icon: CheckCircleIcon,
            title: "File Downloaded",
            color: "#1565c0",
            bg: "#e3f2fd",
            border: "#90caf9",
        },
        cancelled: {
            icon: InfoOutlinedIcon,
            title: "Approval Not Submitted",
            color: "#ed6c02",
            bg: "#fff4e5",
            border: "#ffcc80",
        },
    };

    const renderStatusCard = ({ type, title, description }) => {
        const config = statusConfig[type];
        const StatusIcon = config.icon;

        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: 4,
                    background:
                        "linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%)",
                }}
            >
                <Card
                    sx={{
                        width: "100%",
                        maxWidth: 520,
                        borderRadius: "20px",
                        border: `1px solid ${config.border}`,
                        boxShadow: "0 18px 48px rgba(16,24,40,0.12)",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            px: 3,
                            py: 2.5,
                            background: config.bg,
                            borderBottom: `1px solid ${config.border}`,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <StatusIcon sx={{ fontSize: 74, color: config.color }} />
                    </Box>
                    <CardContent sx={{ px: 3.5, py: 4, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#101828", mb: 1 }}>
                            {title || config.title}
                        </Typography>
                        <Typography sx={{ fontSize: "14px", lineHeight: 1.7, color: "#475467" }}>
                            {description}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    // ==========================================
    // ERROR UI
    // ==========================================

    if (error) {
        return renderStatusCard({
            type: "error",
            description: error,
        });
    }

    // ==========================================
    // MAIN UI
    // ==========================================

    return (
        <>
            {status === "approved" && (
                renderStatusCard({
                    type: "approved",
                    description: message || "The discount action was completed successfully.",
                })
            )}

            {status === "already_approved" && (
                renderStatusCard({
                    type: "already_approved",
                    description: message || "This discount is already approved and published. No further action is needed.",
                })
            )}

            {status === "rejected" && (
                renderStatusCard({
                    type: "rejected",
                    description: message || "The discount request has been rejected.",
                })
            )}

            {status === "already_rejected" && (
                renderStatusCard({
                    type: "already_rejected",
                    description: message || "This discount is already rejected. No further action is needed.",
                })
            )}

            {status === "already_closed" && (
                renderStatusCard({
                    type: "already_closed",
                    description: message || "This discount is already closed. No further action is needed.",
                })
            )}

            {status === "downloading" && (
                renderStatusCard({
                    type: "downloading",
                    description: message || "Your file download should start shortly.",
                })
            )}

            {status === "cancelled" && (
                renderStatusCard({
                    type: "cancelled",
                    description: message || "Open the approve link from the email again to continue.",
                })
            )}

            <Dialog
                open={openApproveDialog}
                maxWidth="sm"
                fullWidth
                onClose={(_, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        return;
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: "18px",
                        boxShadow: "0 20px 50px rgba(16,24,40,0.18)",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1, fontSize: "20px", fontWeight: 700 }}>
                    Approve Discount
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography sx={{ fontSize: "13px", color: "#667085", mb: 2 }}>
                        Approval remarks and a file are optional. A file you upload is saved with this discount.
                    </Typography>

                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={4}
                        margin="dense"
                        label="Approval Remarks (optional)"
                        placeholder="Enter approval remarks"
                        value={remarks}
                        onChange={(e) => {
                            setRemarks(e.target.value);
                            setSubmitError("");
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                            },
                        }}
                    />

                    <Button
                        component="label"
                        variant="outlined"
                        sx={{ mt: 2, textTransform: "none", borderRadius: "10px" }}
                    >
                        {approvalFile ? "Change file" : "Upload file"}
                        <input
                            hidden
                            type="file"
                            onChange={onApprovalFileChange}
                        />
                    </Button>
                    <Typography sx={{ mt: 1, fontSize: "13px", color: "#344054" }}>
                        {approvalFile ? approvalFile.name : "Optional proof file, any type. Maximum 20 MB."}
                    </Typography>
                    {fileError && (
                        <Typography sx={{ mt: 1, fontSize: "13px", color: "#d32f2f" }}>
                            {fileError}
                        </Typography>
                    )}
                    {submitError && (
                        <Typography sx={{ mt: 1, fontSize: "13px", color: "#d32f2f" }}>
                            {submitError}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setOpenApproveDialog(false);
                            setStatus("cancelled");
                            setSMessage("Approval was not submitted. Open the approve link from the email again to continue.");
                        }}
                        sx={{ textTransform: "none", borderRadius: "10px", px: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={approveDiscount}
                        sx={{ textTransform: "none", borderRadius: "10px", px: 2.25 }}
                    >
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRejectDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "18px",
                        boxShadow: "0 20px 50px rgba(16,24,40,0.18)",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1, fontSize: "20px", fontWeight: 700 }}>
                    Reject Discount
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography sx={{ fontSize: "13px", color: "#667085", mb: 2 }}>
                        Please enter the reason for rejection. This message will be used for the discount action response.
                    </Typography>

                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={5}
                        margin="dense"
                        label="Reject Reason"
                        placeholder="Enter reject reason"
                        value={reason}
                        onChange={(e) =>
                            setReason(e.target.value)
                        }
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                            },
                        }}
                    />

                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>

                    <Button
                        variant="outlined"
                        onClick={() =>
                            setOpenRejectDialog(false)
                        }
                        sx={{ textTransform: "none", borderRadius: "10px", px: 2 }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        disabled={!reason}
                        onClick={rejectDiscount}
                        sx={{ textTransform: "none", borderRadius: "10px", px: 2.25 }}
                    >
                        Submit
                    </Button>

                </DialogActions>

            </Dialog>

        </>
    );
}
